import {
  AfterViewInit,
  booleanAttribute,
  ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  Input,
} from '@angular/core';
import { IsActiveMatchOptions, RouterLink, RouterLinkActive } from '@angular/router';
import { LicensePopupService } from 'src/app/shared/services/license-popup/license-popup.service';

@Component({
  selector: 'app-menu-item',
  imports: [RouterLink, RouterLinkActive],
  templateUrl: './menu-item.component.html',
  styleUrl: './menu-item.component.scss',
})
export class MenuItemComponent implements AfterViewInit {
  @Input() public title!: string;
  @Input() public icon?: string;
  @Input() public offset?: number;
  @Input() public link?: string;
  @Input() public count?: number;
  @Input({ transform: booleanAttribute }) public new = false;
  @Input({ transform: booleanAttribute }) public beta = false;
  @Input({ transform: booleanAttribute }) public exact = false;
  @Input({ transform: booleanAttribute }) public disabled = false;

  protected visible: boolean = true;
  protected isChild: boolean = false;
  protected readonly exactMatchOptions: IsActiveMatchOptions = {
    queryParams: 'ignored',
    matrixParams: 'ignored',
    paths: 'exact',
    fragment: 'exact',
  };

  private el = inject(ElementRef);
  private cdr = inject(ChangeDetectorRef);
  private licensePopupService = inject(LicensePopupService);

  ngAfterViewInit(): void {
    // Disable tabbing when inside a collapsed menu
    const parent = this.el.nativeElement.parentNode as HTMLElement;
    this.isChild = parent.classList.contains('collapse');
    if (this.isChild) {
      new ClassWatcher(parent, 'show', (applied) => (this.visible = applied));
      this.visible = false;
    }
    this.cdr.detectChanges();
  }

  protected onClick(event: MouseEvent): void {
    if (!this.disabled) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.licensePopupService.open(this.title);
  }

  protected onKeydown(event: KeyboardEvent): void {
    if (!this.disabled || (event.code !== 'Enter' && event.code !== 'Space')) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.licensePopupService.open(this.title);
  }
}

class ClassWatcher {
  public targetNode: HTMLElement;
  public classToWatch: string;
  public classChangedCallback: (applied: boolean) => void;
  public observer: MutationObserver;
  public lastClassState: boolean;

  constructor(targetNode: HTMLElement, classToWatch: string, classChangedCallback: (applied: boolean) => void) {
    this.targetNode = targetNode;
    this.classToWatch = classToWatch;
    this.classChangedCallback = classChangedCallback;
    this.lastClassState = targetNode.classList.contains(this.classToWatch);
    this.observer = new MutationObserver(this.mutationCallback);
    this.observe();
  }

  public observe(): void {
    this.observer.observe(this.targetNode, { attributes: true });
  }

  public disconnect(): void {
    this.observer.disconnect();
  }

  public mutationCallback = (mutations: MutationRecord[]): void => {
    for (const mutation of mutations) {
      if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
        const currentClassState = (mutation.target as HTMLElement).classList.contains(this.classToWatch);
        if (this.lastClassState !== currentClassState) {
          this.lastClassState = currentClassState;
          this.classChangedCallback(currentClassState);
        }
      }
    }
  };
}
